export const SCHEME_GROUP = {
  width: 184,
  height: 252,
};

export const FIRST_SCENE_GROUP_ID = "1-group";
export const FIRST_SCENE_ID = "1";

export function isFirstSceneNode(node) {
  if (!node) return false;
  return node.type === "first-scene-node" || node.id === FIRST_SCENE_ID;
}

export function isFirstSceneGroup(node) {
  if (!node) return false;
  return node.id === FIRST_SCENE_GROUP_ID;
}

export function isProtectedSchemeNode(node) {
  return isFirstSceneNode(node) || isFirstSceneGroup(node);
}

export const SCHEME_SCENE = {
  x: 0,
  y: 34,
  width: 184,
  height: 132,
};

export const SCHEME_BUTTON = {
  width: 160,
  height: 32,
  x: 12,
  topY: 182,
  bottomY: 216,
};

export function getGroupStyle() {
  return {
    width: SCHEME_GROUP.width,
    height: SCHEME_GROUP.height,
    backgroundColor: "transparent",
    border: "none",
  };
}

export function getNextSceneGroupPosition(nodes = [], parentGroup) {
  const rightmostEdge = nodes.reduce((rightmost, node) => {
    if (node?.type !== "group") return rightmost;
    const x = Number(node.position?.x ?? 0);
    return Number.isFinite(x)
      ? Math.max(rightmost, x + SCHEME_GROUP.width)
      : rightmost;
  }, 0);

  return {
    x: rightmostEdge + 80,
    y: Number(parentGroup?.position?.y ?? 0),
  };
}

export function separateOverlappingSceneGroups(nodes = []) {
  const placedGroups = [];
  let hasMovedGroups = false;

  const nextNodes = nodes.map((node) => {
    if (node?.type !== "group") return node;

    const originalPosition = node.position ?? { x: 0, y: 0 };
    let position = {
      ...originalPosition,
      x: Number(originalPosition.x ?? 0),
      y: Number(originalPosition.y ?? 0),
    };

    while (
      placedGroups.some(
        (placed) =>
          position.x < placed.x + SCHEME_GROUP.width &&
          position.x + SCHEME_GROUP.width > placed.x &&
          position.y < placed.y + SCHEME_GROUP.height &&
          position.y + SCHEME_GROUP.height > placed.y,
      )
    ) {
      const rightmostEdge = placedGroups.reduce(
        (rightmost, placed) =>
          Math.max(rightmost, placed.x + SCHEME_GROUP.width),
        0,
      );
      position = { ...position, x: rightmostEdge + 80 };
      hasMovedGroups = true;
    }

    placedGroups.push(position);
    return position.x === originalPosition.x && position.y === originalPosition.y
      ? node
      : { ...node, position };
  });

  return hasMovedGroups ? nextNodes : nodes;
}

export function arrangeSchemeScenes(nodes = [], edges = []) {
  const sceneNodes = nodes.filter(
    (node) =>
      node?.type === "node-with-toolbar" ||
      node?.type === "first-scene-node" ||
      String(node?.id) === FIRST_SCENE_ID,
  );
  const groupIds = new Set(
    nodes
      .filter((node) => node?.type === "group")
      .map((node) => String(node.id)),
  );
  const sceneGroupIds = new Map();
  const sceneIdsByGroup = new Map();

  sceneNodes.forEach((scene) => {
    const groupId = String(
      scene.parentId ?? scene.parentNode ?? `${scene.id}-group`,
    );
    if (!groupIds.has(groupId)) return;
    sceneGroupIds.set(String(scene.id), groupId);
    sceneIdsByGroup.set(groupId, String(scene.id));
  });

  if (sceneGroupIds.size === 0) return nodes;

  const nodesById = new Map(nodes.map((node) => [String(node.id), node]));
  const connections = new Map(
    [...sceneGroupIds.keys()].map((sceneId) => [sceneId, []]),
  );

  edges.forEach((edge) => {
    const button = nodesById.get(String(edge.source));
    const targetSceneId = String(edge.target);
    if (button?.type !== "input" || !sceneGroupIds.has(targetSceneId)) return;

    const buttonGroupId = String(button.parentId ?? button.parentNode ?? "");
    const sourceSceneId = sceneIdsByGroup.get(buttonGroupId);
    if (!sourceSceneId) return;

    const targets = connections.get(sourceSceneId);
    if (!targets.includes(targetSceneId)) targets.push(targetSceneId);
  });

  const startSceneId = sceneGroupIds.has(FIRST_SCENE_ID)
    ? FIRST_SCENE_ID
    : sceneNodes.find((scene) => scene.type === "first-scene-node")?.id;
  const depthByScene = new Map();
  const queue = [];

  if (startSceneId != null) {
    depthByScene.set(String(startSceneId), 0);
    queue.push(String(startSceneId));
  }

  for (let index = 0; index < queue.length; index += 1) {
    const sceneId = queue[index];
    const nextDepth = depthByScene.get(sceneId) + 1;
    (connections.get(sceneId) ?? []).forEach((targetSceneId) => {
      if (depthByScene.has(targetSceneId)) return;
      depthByScene.set(targetSceneId, nextDepth);
      queue.push(targetSceneId);
    });
  }

  const maxDepth = Math.max(0, ...depthByScene.values());
  const orphanDepth = maxDepth + 1;
  const columns = new Map();

  sceneNodes.forEach((scene) => {
    const sceneId = String(scene.id);
    const depth = depthByScene.get(sceneId) ?? orphanDepth;
    const groupId = sceneGroupIds.get(sceneId);
    if (!groupId) return;
    if (!columns.has(depth)) columns.set(depth, []);
    columns.get(depth).push(groupId);
  });

  const positionsByGroup = new Map();
  columns.forEach((groupColumn, depth) => {
    groupColumn.forEach((groupId, index) => {
      positionsByGroup.set(groupId, {
        x: depth * (SCHEME_GROUP.width + 120),
        y: index * (SCHEME_GROUP.height + 64),
      });
    });
  });

  let hasMovedGroups = false;
  const arrangedNodes = nodes.map((node) => {
    if (node?.type !== "group") return node;
    const position = positionsByGroup.get(String(node.id));
    if (!position) return node;

    if (
      Number(node.position?.x) === position.x &&
      Number(node.position?.y) === position.y
    ) {
      return node;
    }

    hasMovedGroups = true;
    return { ...node, position };
  });

  return hasMovedGroups ? arrangedNodes : nodes;
}

export function getSceneStyle() {
  return {
    width: SCHEME_SCENE.width,
    height: SCHEME_SCENE.height,
    backgroundColor: "transparent",
    border: "none",
  };
}

export function getButtonStyle() {
  return {
    width: SCHEME_BUTTON.width,
    height: SCHEME_BUTTON.height,
    backgroundColor: "transparent",
    border: "none",
  };
}

export const TRAINER_SCHEME_GROUP = {
  width: 184,
  height: 292,
};

export const TRAINER_BUTTON_SLOTS = ["first", "second", "third", "fourth"];

export const TRAINER_BUTTON_Y = {
  first: 170,
  second: 192,
  third: 214,
  fourth: 236,
};

export function getTrainerGroupStyle() {
  return {
    width: TRAINER_SCHEME_GROUP.width,
    height: TRAINER_SCHEME_GROUP.height,
    backgroundColor: "transparent",
    border: "none",
  };
}

export function getTrainerButtonPosition(slot) {
  return {
    x: SCHEME_BUTTON.x,
    y: TRAINER_BUTTON_Y[slot] ?? TRAINER_BUTTON_Y.first,
  };
}

export function getNextTrainerButtonSlot(existingButtons) {
  const used = new Set(
    (existingButtons ?? []).map((node) => node.data?.buttonPosiiton)
  );
  return TRAINER_BUTTON_SLOTS.find((slot) => !used.has(slot)) ?? null;
}

export function getButtonPosition(slot) {
  if (TRAINER_BUTTON_Y[slot] != null) {
    return getTrainerButtonPosition(slot);
  }
  return {
    x: SCHEME_BUTTON.x,
    y: slot === "top" ? SCHEME_BUTTON.topY : SCHEME_BUTTON.bottomY,
  };
}

export function isTrainerButtonSlot(slot) {
  return TRAINER_BUTTON_SLOTS.includes(slot);
}

export function stripSchemeTransient(nodes) {
  return nodes.map((node) => {
    const cleaned = { ...node };

    if (cleaned.data?.isSceneSelected !== undefined) {
      const { isSceneSelected, isActive, ...rest } = cleaned.data;
      cleaned.data = rest;
    } else if (cleaned.data?.isActive !== undefined) {
      const { isActive, ...rest } = cleaned.data;
      cleaned.data = rest;
    }

    if (cleaned.type === "group") {
      cleaned.className = "scheme-group";
      if (cleaned.style) {
        const { zIndex, ...restStyle } = cleaned.style;
        cleaned.style = restStyle;
      }
    }

    return cleaned;
  });
}

export function normalizeSchemeNodes(nodes) {
  if (!nodes?.length) return nodes;

  return nodes.map((node) => {
    if (node.type === "group") {
      const normalized = {
        ...node,
        dragHandle: ".scheme-group-drag-handle",
        draggable: true,
        className: "scheme-group",
        style: { ...node.style, ...getGroupStyle() },
      };

      if (isFirstSceneGroup(node)) {
        normalized.deletable = false;
      }

      return normalized;
    }

    if (node.type === "input" && node.parentId) {
      const slot = node.data?.buttonPosiiton ?? "bottom";
      const resolvedSlot = isTrainerButtonSlot(slot)
        ? slot
        : slot === "top"
          ? "top"
          : "bottom";
      return {
        ...node,
        className: "scheme-button",
        position: getButtonPosition(resolvedSlot),
        style: getButtonStyle(),
        draggable: false,
      };
    }

    if (node.type === "node-with-toolbar" || node.type === "first-scene-node") {
      const normalized = {
        ...node,
        className: "scheme-scene",
        position: { x: SCHEME_SCENE.x, y: SCHEME_SCENE.y },
        style: getSceneStyle(),
        draggable: false,
      };

      if (isFirstSceneNode(node)) {
        normalized.deletable = false;
      }

      return normalized;
    }

    return node;
  });
}
